import { useEffect, useState } from 'react';
import { useGraphStore } from '@/store/graphStore';
import { threatColor } from '@/types/dns';
import type { DNSNode } from '@/types/dns';

interface RiskScoreData {
  score: number;
  level: string;
  factors: Record<string, number>;
  flags: string[];
}

interface ReverseIpDomain {
  domain: string;
  tld: string;
  threat_score: number | null;
  risk_score: number | null;
  record_type: string;
  first_seen_at: string | null;
  last_seen_at: string | null;
}

interface SslCert {
  fingerprint: string;
  subject_cn: string;
  issuer_cn: string;
  issuer_org: string;
  not_before: string | null;
  not_after: string | null;
  days_remaining: number | null;
  key_algorithm: string;
  key_bits: number;
  is_wildcard: boolean;
  is_ev: boolean;
  is_self_signed: boolean;
  san_count: number;
}

// ---------------------------------------------------------------------------
// Field row
// ---------------------------------------------------------------------------

function FieldRow({ label, value }: { label: string; value: string | number | boolean | null | undefined }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b" style={{ borderColor: '#1e2830' }}>
      <span className="text-xs uppercase tracking-wider" style={{ color: '#8b949e' }}>
        {label}
      </span>
      <span className="text-sm break-all font-mono" style={{ color: '#c9d1d9' }}>
        {String(value)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Threat badge
// ---------------------------------------------------------------------------

function ThreatBadge({ score }: { score: number }) {
  const color = threatColor(score);
  const label =
    score < 20 ? 'CLEAN' : score < 50 ? 'SUSPICIOUS' : score < 80 ? 'HIGH RISK' : 'MALICIOUS';
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-bold"
      style={{ background: `${color}22`, color, border: `1px solid ${color}66` }}
    >
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {score} — {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Risk factors bar chart
// ---------------------------------------------------------------------------

function RiskFactors({ factors }: { factors: Record<string, number> }) {
  const sorted = Object.entries(factors).sort(([, a], [, b]) => b - a);
  const max = Math.max(...sorted.map(([, v]) => v), 1);
  return (
    <div className="py-2 border-b" style={{ borderColor: '#1e2830' }}>
      <span className="text-xs uppercase tracking-wider block mb-2" style={{ color: '#8b949e' }}>
        Risk Factors
      </span>
      {sorted.map(([name, value]) => (
        <div key={name} className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono w-24 truncate" style={{ color: '#8b949e' }}>{name}</span>
          <div className="flex-1 h-2 rounded" style={{ background: '#21262d' }}>
            <div
              className="h-full rounded"
              style={{ width: `${(value / max) * 100}%`, background: '#58a6ff' }}
            />
          </div>
          <span className="text-xs font-mono w-6 text-right" style={{ color: '#c9d1d9' }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SSL Cert section
// ---------------------------------------------------------------------------

function SslCertSection({ domain }: { domain: string }) {
  const [cert, setCert] = useState<SslCert | null | 'loading'>('loading');

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/v1/intel/ssl-cert/${encodeURIComponent(domain)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setCert(d?.cert ?? null); })
      .catch(() => { if (!cancelled) setCert(null); });
    return () => { cancelled = true; };
  }, [domain]);

  if (cert === 'loading') return null;
  if (!cert) return (
    <div className="py-2 border-b" style={{ borderColor: '#1e2830' }}>
      <span className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#8b949e' }}>SSL Certificate</span>
      <span className="text-xs" style={{ color: '#8b949e' }}>No cert on file</span>
    </div>
  );

  const daysLeft = cert.days_remaining;
  const certColor = daysLeft === null ? '#8b949e' : daysLeft <= 0 ? '#f85149' : daysLeft <= 14 ? '#f0883e' : daysLeft <= 30 ? '#e3b341' : '#56d364';
  const expLabel = daysLeft === null ? '—' : daysLeft <= 0 ? `EXPIRED ${Math.abs(daysLeft)}d ago` : `${daysLeft}d remaining`;

  return (
    <div className="py-2 border-b" style={{ borderColor: '#1e2830' }}>
      <span className="text-xs uppercase tracking-wider block mb-2" style={{ color: '#8b949e' }}>SSL Certificate</span>
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center">
          <span className="text-xs font-mono" style={{ color: '#c9d1d9' }}>{cert.subject_cn}</span>
          {cert.is_wildcard && (
            <span className="text-xs px-1 rounded" style={{ background: '#58a6ff22', color: '#58a6ff', border: '1px solid #58a6ff44' }}>WILDCARD</span>
          )}
          {cert.is_ev && (
            <span className="text-xs px-1 rounded" style={{ background: '#56d36422', color: '#56d364', border: '1px solid #56d36444' }}>EV</span>
          )}
        </div>
        <div className="text-xs font-mono" style={{ color: '#8b949e' }}>Issuer: {cert.issuer_cn}</div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: certColor }} />
          <span className="text-xs font-mono" style={{ color: certColor }}>{expLabel}</span>
        </div>
        <div className="text-xs font-mono" style={{ color: '#8b949e' }}>
          {cert.key_algorithm} {cert.key_bits}-bit · {cert.san_count} SAN{cert.san_count !== 1 ? 's' : ''}
        </div>
        {cert.is_self_signed && (
          <span className="text-xs" style={{ color: '#f85149' }}>⚠ Self-signed</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reverse-IP section (IP nodes only)
// ---------------------------------------------------------------------------

function ReverseIpSection({ ip }: { ip: string }) {
  const [data, setData] = useState<{ domains: ReverseIpDomain[]; count: number } | null | 'loading'>('loading');

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/v1/intel/reverse-ip/${encodeURIComponent(ip)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setData(d ?? null); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [ip]);

  if (data === 'loading') return (
    <div className="py-2 border-b" style={{ borderColor: '#1e2830' }}>
      <span className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#8b949e' }}>Co-hosted Domains</span>
      <span className="text-xs" style={{ color: '#8b949e' }}>Loading…</span>
    </div>
  );

  if (!data || data.count === 0) return (
    <div className="py-2 border-b" style={{ borderColor: '#1e2830' }}>
      <span className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#8b949e' }}>Co-hosted Domains</span>
      <span className="text-xs" style={{ color: '#8b949e' }}>None on file</span>
    </div>
  );

  return (
    <div className="py-2 border-b" style={{ borderColor: '#1e2830' }}>
      <span className="text-xs uppercase tracking-wider block mb-2" style={{ color: '#8b949e' }}>
        Co-hosted Domains ({data.count})
      </span>
      <div className="flex flex-col gap-0.5" style={{ maxHeight: 200, overflowY: 'auto' }}>
        {data.domains.map((d) => {
          const score = d.threat_score ?? 0;
          const color = threatColor(score);
          return (
            <div
              key={d.domain}
              className="flex items-center justify-between gap-2 px-1.5 py-1 rounded"
              style={{ background: '#0d111788' }}
            >
              <span className="text-xs font-mono truncate" style={{ color: '#c9d1d9', maxWidth: 180 }}>
                {d.domain}
              </span>
              <span
                className="text-xs font-mono flex-shrink-0"
                style={{ color }}
              >
                {score}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel body
// ---------------------------------------------------------------------------

function NodeFields({ node }: { node: DNSNode }) {
  return (
    <div className="flex flex-col gap-0">
      <FieldRow label="ID" value={node.id} />
      <FieldRow label="Type" value={node.type.toUpperCase()} />
      <FieldRow label="Label" value={node.label} />

      <div className="py-2 border-b" style={{ borderColor: '#1e2830' }}>
        <span className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#8b949e' }}>
          Threat Score
        </span>
        <ThreatBadge score={node.threatScore} />
      </div>

      {node.country && <FieldRow label="Country" value={node.country} />}
      {node.org && <FieldRow label="Organisation" value={node.org} />}
      {node.abuse_score !== undefined && (
        <FieldRow label="Abuse Score" value={node.abuse_score} />
      )}
      {node.feeds && node.feeds.length > 0 && (
        <div className="py-2 border-b" style={{ borderColor: '#1e2830' }}>
          <span className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#8b949e' }}>
            Feed Hits
          </span>
          <div className="flex flex-wrap gap-1">
            {node.feeds.map((feed) => (
              <span
                key={feed}
                className="text-xs px-1.5 py-0.5 rounded"
                style={{ background: '#ff444422', color: '#ff4444', border: '1px solid #ff444444' }}
              >
                {feed}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* IP-specific: reverse-IP co-hosted domains */}
      {node.type === 'ip' && <ReverseIpSection ip={node.label} />}

      {/* Domain-specific: SSL cert */}
      {node.type === 'domain' && <SslCertSection domain={node.label} />}

      {/* Raw god daemon payload — shown as collapsible JSON */}
      {node.raw && Object.keys(node.raw).length > 0 && (
        <details className="py-2">
          <summary
            className="text-xs uppercase tracking-wider cursor-pointer select-none"
            style={{ color: '#8b949e' }}
          >
            Raw Enrichment Data
          </summary>
          <pre
            className="mt-2 text-xs overflow-x-auto rounded p-2"
            style={{ background: '#0d1117', color: '#c9d1d9', maxHeight: '300px', overflowY: 'auto' }}
          >
            {JSON.stringify(node.raw, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function NodeDetailPanel() {
  const selectedNode = useGraphStore((state) => state.selectedNode);
  const setSelectedNode = useGraphStore((state) => state.setSelectedNode);
  const [riskScore, setRiskScore] = useState<RiskScoreData | null>(null);

  useEffect(() => {
    const label = selectedNode?.label;
    if (!label) { setRiskScore(null); return; }
    let cancelled = false;
    fetch('/api/v1/risk/score', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain: label }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setRiskScore(d?.score ?? null); })
      .catch(() => { if (!cancelled) setRiskScore(null); });
    return () => { cancelled = true; };
  }, [selectedNode?.label]);

  if (!selectedNode) return null;

  return (
    <aside
      className="absolute top-0 right-0 h-full flex flex-col overflow-hidden z-10"
      style={{
        width: '320px',
        background: '#161b22',
        borderLeft: '1px solid #30363d',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.4)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: '#30363d' }}
      >
        <span className="text-sm font-bold" style={{ color: '#58a6ff' }}>
          Node Detail
        </span>
        <button
          onClick={() => setSelectedNode(null)}
          className="text-lg leading-none"
          style={{ color: '#8b949e', background: 'none', border: 'none', cursor: 'pointer' }}
          aria-label="Close panel"
        >
          x
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4">
        <NodeFields node={selectedNode} />
        {riskScore && Object.keys(riskScore.factors ?? {}).length > 0 && (
          <RiskFactors factors={riskScore.factors} />
        )}
      </div>
    </aside>
  );
}
